import { AlertDialog, Button, Text } from "@overmap-ai/blocks"
import { memo, useCallback } from "react"

import { openLogin } from "../../sdk"
import { useAppSelector } from "../../store"
import { selectSessionExpired } from "../../store/selectors"

/**
 * Tells the user their session ended, and gives them the one way back.
 *
 * It has no cancel: there is nothing useful to do in the app without a session, and dismissing it would leave every
 * request failing with no explanation on screen. Being controlled with no `onOpenChange` is what makes that stick --
 * Radix asks to close, nothing listens, and Escape does nothing.
 *
 * NOTE: Unsynced work is safe across this. Signing in is a page navigation, not a sign-out, so the outbox is still in
 *  IndexedDB when the user comes back, and it drains once the requests stop coming back 401.
 */
export const SessionExpiredDialog = memo(() => {
	const sessionExpired: boolean = useAppSelector(selectSessionExpired)

	const handleSignIn = useCallback((): void => {
		openLogin()
	}, [])

	return (
		<AlertDialog.Root open={sessionExpired}>
			<AlertDialog.Content size="sm">
				<div className="flex flex-col gap-4">
					<div className="flex flex-col gap-1">
						<AlertDialog.Title>Session expired</AlertDialog.Title>
						<AlertDialog.Description asChild>
							<Text accentColor="base">
								You have been signed out. Please sign in to resume syncing changes.
							</Text>
						</AlertDialog.Description>
					</div>

					<div className="flex justify-end gap-2">
						<AlertDialog.Action asChild onClick={handleSignIn}>
							<Button variant="solid" size="sm">
								Sign in
							</Button>
						</AlertDialog.Action>
					</div>
				</div>
			</AlertDialog.Content>
		</AlertDialog.Root>
	)
})

SessionExpiredDialog.displayName = "SessionExpiredDialog"
